import { Store } from '@reduxjs/toolkit';
import BaseModule from 'sdk/base/base-module';

import { ActionTypes, sliceName } from './userConfig';
import * as selectors from './userSelector';
import { User as FBUser } from 'firebase/auth';
import FirebaseApi from '../firebase';
import { FIREBASE_TABLES } from '../firebase/firebaseConfig';
import { AppUser, AppUserDisplayInfo } from './userInterface';

/**
 * @author Ahmad Igbaryya
 * @description Manage Users 
 *
 * @creationDate Mon Jan 27 2025
 */
export default class UserApi extends BaseModule {
    public static instance: UserApi;
    private firebaseApi = FirebaseApi.getInstance();
    constructor(store: Store) {
        if (UserApi.instance) {
            return UserApi.instance;
        }
        super(store, ActionTypes, sliceName);
        UserApi.instance = this;
    }

    public static getInstance() {
        return UserApi.instance;
    }


    getAppCurrentUser = () => {
        return selectors.currentAppUserSelector(this.store.getState());
    };
    loadCurrentUser = () => {
        const uid = window.localStorage.getItem('USER');
        if (!uid) {
            return () => {};
        }
        this.firebaseApi.registerCall(`${FIREBASE_TABLES.USERS}/${uid}`, (snapshot: any) => {
            const user: AppUser = snapshot.val();
            this.dispatchAction(ActionTypes.UPDATE_CURRENT_APP_USER, { uid, ...user });
        });
    };

    retrieveUserDisplayInfo = async (uid?: string) => {
        if (!uid) {
            return null;
        }
        const usersDisplayInfo = (this.getUsersDisplayInfo() as Record<string, AppUserDisplayInfo>) || {};
        if (usersDisplayInfo?.[uid]) {
            return usersDisplayInfo[uid]; 
        }
        const userInfo = await this.firebaseApi.asyncRead(`${FIREBASE_TABLES.USERS}/${uid}/displayInfo`);
        if (!userInfo) {
            return null;
        }
        usersDisplayInfo[uid] = userInfo;
        this.dispatchAction(ActionTypes.UPDATE_USERS_DISPLAY_INFO, usersDisplayInfo);
        return userInfo;
    };

    getUsersDisplayInfo = (uid?: string) => {
        const allUsers = selectors.usersDisplayInfoSelector(this.store.getState());
        if (uid) {
            return allUsers?.[uid];
        }
        return allUsers || {};
    };
    initUser = async (user: FBUser) => {
        const uid = window.localStorage.getItem('USER');
        if (!user?.uid || user.uid !== uid) {
            // eslint-disable-next-line no-console
            console.error('User not found');
            return; 
        }
        const { email, photoURL: avatar, displayName: fullName } = user;
        const userFromFB = await this.retrieveUser(uid);
        if (userFromFB && userFromFB.displayInfo) {
            return;
        }
        const newUser: AppUser = {
            displayInfo: {
                email: email || '',
                title: 'Guest',
                avatar: avatar || undefined,
                fullName: fullName || ''
            },
            uid,
            canWrite: uid === 'XpvYwTgiXNgJO1tIjQEXtbg8vrt1',
            creationDate: this.firebaseApi.getServerTimestamp()
        };
        this.firebaseApi.asyncWrite(`${FIREBASE_TABLES.USERS}/${uid}`, newUser);
    };

    registerCurrentUser = () => {
        
    };

    retrieveUser = async (uid?: string | null): Promise<AppUser> => {
        if (!uid) {
            throw new Error('UID is required');
        }
        const user = await this.firebaseApi.asyncRead(`${FIREBASE_TABLES.USERS}/${uid}`);
        const storedUid = window.localStorage.getItem('USER');
        if (uid === storedUid) {
            this.dispatchAction(ActionTypes.UPDATE_CURRENT_APP_USER, { uid, ...user });
        }
        return user;
    };
    getModuleErrors = () => {
        return selectors.userErrorsSelector(this.store.getState());
    };

    clearCurrentUser = () => {
        this.dispatchAction(ActionTypes.UPDATE_CURRENT_APP_USER, null);
    };

    updateUser = async (updates: Partial<AppUserDisplayInfo>, avatar?: File | null) => {
        const uid = window.localStorage.getItem('USER');
        if (!uid) {
            throw new Error('User not found');
        }

        let avatarUrl: string | undefined;
        
        // Upload avatar if provided
        if (avatar) {
            const uploadResult = await this.firebaseApi.asyncStorageUpload(
                `${FIREBASE_TABLES.USERS}/${uid}/avatar`,
                avatar,
                true
            );
            avatarUrl = typeof uploadResult === 'string' ? uploadResult : undefined;
        }

        const displayInfoUpdates: Partial<AppUserDisplayInfo> = {
            ...updates,
            ...(avatarUrl && { avatar: avatarUrl })
        };

        await this.firebaseApi.asyncUpdate(
            `${FIREBASE_TABLES.USERS}/${uid}/displayInfo`,
            displayInfoUpdates
        );

        // Refresh current user data
        await this.retrieveUser(uid);
        
        return displayInfoUpdates;
    };
}

